import React from 'react';
import {
  formatDate,
} from 'utils/date';
import {
  useMedia,
} from 'core/hooks';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import RowActions from 'pages/Dashboard/pages/Billing/Payments/RowActions';
import {
  PatientPaymentTransaction,
} from 'pages/Dashboard/pages/Billing/types';
import Name from 'pages/Dashboard/components/Name';
import PaymentCell from 'pages/Dashboard/pages/Billing/Payments/columns/PaymentCell';
import AmountTag, {
  defaultNegativeClass,
} from 'pages/Dashboard/pages/Billing/AmountTag';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import WebhookIcon from '@mui/icons-material/Webhook';
import Tag from 'core/components/Tag';
import {
  PatientPaymentTransactionType,
} from 'dtos/patientPaymentTransactionType';
import {
  Row,
} from '@tanstack/react-table';
import {
  getPatientChartPath,
} from 'pages/Dashboard/pages/Charts/utils';
import {
  notEmptyString,
} from '@populateui/utils';
import isNil from 'lodash/isNil';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';

type PayerCellProps = {
  row: Row<PatientPaymentTransaction>;
};

function PayerCell({
  row,
}: PayerCellProps) {
  const { getMediaPathById } = useMedia();
  const src = getMediaPathById(row.original.patient?.patientId ?? 0) ?? '';

  return (
    <Name
      className="overflow-hidden flex items-end !w-fit"
      classes={{
        label: '!w-fit',
      }}
      {...row.original.patient?.formattedNames}
      placeholder={DEFAULT_EMPTY_VALUE}
      link={getPatientChartPath(row.original.patient?.patientId)}
      image={{ src }}
    />
  );
}

type CreatedByCellProps = {
  row: Row<PatientPaymentTransaction>;
};

function CreatedByCell({
  row: { original: { createdByUser, userAlias, isFromPatientPortal } },
}: CreatedByCellProps) {
  const isOnlinePayment = /online payment/i.test(userAlias ?? '');
  const patientPortalPayment = isFromPatientPortal ? 'Patient Portal' : undefined;
  const Icon = isFromPatientPortal ? WebhookIcon : CurrencyExchangeIcon;
  const hasOverrideName = !isNil(patientPortalPayment) || notEmptyString(userAlias);
  const src = useUserProfilePicturePath(createdByUser?.userId);

  return (
    <Name
      name={patientPortalPayment ?? userAlias}
      {...(hasOverrideName ? {} : createdByUser?.formattedNames ?? {})}
      image={isOnlinePayment
        ? <Icon className="!w-5 !h-5 !text-gray-600" />
        : { src }}
    />
  );
}

const columns: ColumnDef<PatientPaymentTransaction>[] = [{
  id: 'paymentType',
  header: 'Type',
  size: 90,
  cell: ({ row: { original: { type } } }) => (
    <div>
      <Tag
        tagName={type}
        className="w-fit !px-2 !py-1 font-semibold"
      />
    </div>
  ),
}, {
  id: 'payer',
  size: 250,
  columnTitle: 'Payer',
  onClick(e) { e.stopPropagation(); },
  cell: ({ row }) => <PayerCell row={row} />,
}, {
  id: 'comment',
  meta: { autoSized: true },
  columnTitle: 'Comment',
  cell: ({ row: { original: { comment } } }) => (
    <div className="w-full flex-wrap text-pretty italic pr-10">{comment ?? '—'}</div>
  ),
}, {
  id: 'paymentInfo',
  header: 'Payment Method',
  size: 150,
  cell: ({ row }) => (
    <PaymentCell
      number={row.original?.patientPaymentId?.toString() ?? row.original?.patientPaymentRefundId?.toString() ?? ''}
      type={row.original?.transactionMethod?.discriminator ?? undefined}
    />
  ),
}, {
  id: 'amount',
  header: 'Amount',
  size: 120,
  cell: ({ row: { original: { transactionMethod, type } } }) => (
    <AmountTag
      value={transactionMethod?.amount}
      useColors
      classes={type === PatientPaymentTransactionType.Refund
        ? { positive: defaultNegativeClass }
        : undefined}
    />
  ),
}, {
  id: 'createdOn',
  size: 100,
  header: 'Created on',
  cell: ({ row }) => formatDate(row.original?.createdOn ?? ''),
}, {
  id: 'createdBy',
  size: 200,
  header: 'Created by',
  cell: ({ row }) => <CreatedByCell row={row} />,
}, {
  id: 'actions',
  header: '',
  size: 104,
  cell: ({ row }) => (
    <RowActions {...row.original} />
  ),
}];

export default columns;
